import sessionActions from '../actions/SessionActions';
import storeManager from '../store/StoreManager';
import navigationService from './NavigationService';
import alertService from './AlertService';
import { t } from './i18nService';
import settingsReader from '../store/readHelpers/SettingsReader';
import apiService from './api/ApiService';
import timeUtils from '../qti/utils/TimeUtils';
import sessionReader from '../store/readHelpers/SessionReader';
import utilService from './UtilService';
import uiActions from '../actions/UIActions';
import AudioHowlerPlayer from '../loading/resources/AudioHowlerPlayer';
import mediaFinder from '../loading/resources/mediaFinder';
import parentTestService from './ParentTestService';
import timeService from './TimeService';
import scorableItemNumberingService from './questionView/ScorableItemNumberingService';
import assessmentReader from '../store/readHelpers/AssessmentReader';

let currentAudioPlayer;

const backgroundAudioService = {
    postPositionSetInterval: undefined,
    postPositionTimeout: 20000,

    getBackgroundAudioModel(assessmentRunId) {
        const assessment = parentTestService.getChildOrParentAssessment(assessmentRunId);
        return mediaFinder.getMediaModelForBackgroundAudio(assessment, assessmentRunId);
    },

    hasBackgroundAudio(assessmentRunId) {
        return !!this.getBackgroundAudioModel(assessmentRunId);
    },

    getFormatFromUrl(url) {
        return url.includes('opus') ? 'opus' : 'mp3';
    },

    start(assessmentRunId) {
        if (!this.hasBackgroundAudio(assessmentRunId)) {
            console.warn('No audio resource found');
            return;
        }
        console.info('Background audio initialising', assessmentRunId);
        currentAudioPlayer = AudioHowlerPlayer.getInstanceForAssessmentRunId(assessmentRunId)
            || this.createAssessmentHowlerPlayerInstance(assessmentRunId);

        console.info('Background audio: playAudio', assessmentRunId);
        currentAudioPlayer.playAudio(this.getCurrentTime());
        storeManager.dispatch(uiActions.setBackgroundAudioStarted(true));
        if (sessionReader.isContextExam()) {
            this.startPostingPosition();
        }
    },

    createAssessmentHowlerPlayerInstance(assessmentRunId) {
        const { sources, partStartTimes } = this.getBackgroundAudioModel(assessmentRunId);

        const urls = sources.map(source => source.url);
        const formats = urls.map(url => this.getFormatFromUrl(url));
        return AudioHowlerPlayer.createInstance(assessmentRunId, this.onEnded, urls, formats, partStartTimes);
    },

    async playAudioForNonChildTest() {
        const { assessment } = storeManager.getState();
        const questionSet = sessionReader.isModeSection() && assessment.sections.length ? assessment.sections[0] : assessment;
        const { sources, partStartTimes } = mediaFinder.getMediaModelForBackgroundAudio(questionSet);
        if (!sources) {
            return;
        }
        const urls = sources.map(source => source.url);
        const formats = urls.map(url => this.getFormatFromUrl(url));
        currentAudioPlayer = AudioHowlerPlayer.createInstance(-1, this.onEnded, urls, formats, partStartTimes);
        storeManager.dispatch(uiActions.setUseBackgroundAudioOverlay(true));
    },

    onEnded() {
        console.info('Background audio ended');
        storeManager.dispatch(uiActions.setUseBackgroundAudioOverlay(false));
        backgroundAudioService.stopPostingPosition(true);
    },

    getCurrentTime() {
        const backgroundAudioPosition = utilService.getIntOr(sessionReader.getSessionInfoValue('backgroundAudioPosition'), 0);
        const extraSecondsToRewind = 10; // Starting from latest posted background audio position minus ten seconds.
        if (backgroundAudioPosition > extraSecondsToRewind) {
            return backgroundAudioPosition - extraSecondsToRewind;
        }
        return 0;
    },

    startAudioPlayFromOverlay() {
        if (!currentAudioPlayer) {
            console.warn('No audio player initialized when calling startAudioPlayFromOverlay');
            return;
        }
        storeManager.dispatch(uiActions.setBackgroundAudioStarted(true));
        currentAudioPlayer.resumeCurrentAudio();
        if (sessionReader.isContextExam()) {
            this.startPostingPosition();
        }
    },

    cancelAudioPlayFromOverlay() {
        if (!sessionReader.isContextPreview()) {
            return;
        }
        storeManager.dispatch(uiActions.setBackgroundAudioCanceled(true));
    },

    startPostingPosition() {
        clearInterval(this.postPositionSetInterval);
        this.postPositionSetInterval = setInterval(() => {
            this.postBackgroundAudioPosition();
        }, this.postPositionTimeout);
    },

    postBackgroundAudioPosition() {
        if (!currentAudioPlayer) {
            console.warn('No audio player initialized when calling postBackgroundAudioPosition');
            return;
        }
        const assessmentRunId = sessionReader.getAssessmentRunId();
        const backgroundAudioPosition = Math.round(currentAudioPlayer.getCurrentAudioTime());
        const data = {
            assessmentRunId,
            backgroundAudioPosition,
        };
        apiService.setBackgroundAudioPosition(data);
    },

    stopPostingPosition(postLastPosition = true) {
        storeManager.dispatch(uiActions.setBackgroundAudioStarted(false));
        clearInterval(this.postPositionSetInterval);
        if (postLastPosition && sessionReader.isContextExam()) {
            this.postBackgroundAudioPosition();
        }
    },

    stopAndRemoveAudio() {
        if (!currentAudioPlayer) {
            console.warn('No audio player initialized when calling stopAndRemoveAudio');
            return;
        }
        currentAudioPlayer.stopAndRemoveCurrentAudio();
        storeManager.dispatch(uiActions.setBackgroundAudioStarted(false));
        this.stopPostingPosition(false);
    },

    async nudgePlayerInstanceCreation(assessmentRunId) {
        return new Promise((resolve) => {
            setTimeout(() => {
                const startTime = Date.now();
                this.createAssessmentHowlerPlayerInstance(assessmentRunId);
                const playerCreationDelay = Math.ceil((Date.now() - startTime) / 1000);

                // Add logs if creating time is more then 3s
                // Ideally it should be executed immediately. Otherwise need to log it to investigate later.
                if (playerCreationDelay > 3) {
                    console.warn(`Background audio player creation took ${playerCreationDelay}s, assessmentRunId - ${assessmentRunId}`);
                }

                resolve();
            });
        });
    },

    pause() {
        if (currentAudioPlayer && !currentAudioPlayer.audioHasEnded()) {
            currentAudioPlayer.pauseCurrentAudio();
            this.stopPostingPosition();
        }
    },

    resume() {
        if (currentAudioPlayer && !currentAudioPlayer.audioHasEnded()) {
            currentAudioPlayer.resumeCurrentAudio();
            storeManager.dispatch(uiActions.setBackgroundAudioStarted(true));
            this.startPostingPosition();
        }
    },

    rewindByMinutes(minutes) {
        if (!currentAudioPlayer) {
            console.error('No audio player initialized when calling rewind');
            return;
        }

        const parsedMinutes = isNaN(minutes) ? 0 : minutes;
        const secondsToRewind = parsedMinutes * 60;
        let currentTime = currentAudioPlayer.getCurrentAudioTime();
        if (currentTime) {
            const rewindPositionErrorMarginInSeconds = (this.postPositionTimeout / 1000) + 10;
            currentTime = Math.max(currentTime - (secondsToRewind + rewindPositionErrorMarginInSeconds), 0);

            const timeout = settingsReader.getRewindAudioTimeout() / 1000;
            const alert = t('options.rewind-audio.rewindAlert', { minutes: parsedMinutes, timeout });

            this.rewindToPosition(currentTime, alert);
        }
    },

    rewindBySection(partId, extraTime) {
        if (!currentAudioPlayer) {
            console.error('No audio player initialized when calling rewind');
            return;
        }

        const rewindPosition = currentAudioPlayer.getAudioPartTime(partId);
        if (rewindPosition === null) {
            console.error('Rewind part time does not exist');
            return;
        }

        if (rewindPosition > currentAudioPlayer.getCurrentAudioTime()) {
            console.error('Not possible to rewind to that position because current position is earlier');
            return;
        }

        const timeout = settingsReader.getRewindAudioTimeout();
        const extraMinutes = utilService.getIntOr(extraTime, 0);
        const extraTimeMessage = extraMinutes === 1
            ? t('options.addExtraMinuteAddedConfirmation')
            : t('options.addExtraTimeAddedConfirmation', { minutes: extraMinutes });

        const rewindSection = assessmentReader.getSectionByAdditionalResource(partId);
        if (!rewindSection) {
            console.error('Rewind section can\'t be found');
            return;
        }

        const sectionName = scorableItemNumberingService.getStimulusFormatNumber(rewindSection.id);
        const alert = `${t('alert.sectionAudioRewound', { sectionName, timeout: timeout / 1000 })} ${extraTimeMessage}`;

        timeService.addTimeInMinutes(extraMinutes);
        this.rewindToPosition(rewindPosition, alert);
    },

    rewindToPosition(position, alert) {
        // Pause during alert
        currentAudioPlayer.pauseCurrentAudio();
        const timeout = settingsReader.getRewindAudioTimeout();
        alertService.showAlert(alert, { timeout });

        storeManager.dispatch(uiActions.setBackgroundAudioStarted(false));
        setTimeout(() => {
            currentAudioPlayer.seekCurrentAudio(position);
            storeManager.dispatch(uiActions.setBackgroundAudioStarted(true));
            timeService.fetchTimeRemaining(true, true);
        }, timeout);

        // continue posting audio position after rewind
        if (sessionReader.isContextExam()) {
            this.startPostingPosition();
        }
    },

    invigilatorRewind(minutes) {
        storeManager.dispatch(sessionActions.toggleInvigilatorAuthorized(false));
        navigationService.exitFromOptions();
        this.rewindByMinutes(minutes);
        apiService.notifyClientEvent('client_audio_rewind', minutes, true).then(() => {
            console.log('Client rewind event sent success');
        }).catch(() => {
            console.warn('Client rewind event sent failed');
        });
    },

    parseTime(time) {
        const { minutes, seconds } = timeUtils.getTimeUnitsFromDuration(time * 1000);
        const formattedMinutes = minutes < 10 ? `0${minutes}` : minutes;
        const formattedSeconds = seconds < 10 ? `0${seconds}` : seconds;
        return `${formattedMinutes}:${formattedSeconds}`;
    },

    showBackgroundAudioTime(duration = currentAudioPlayer.getCurrentAudioDuration(), currentTime = currentAudioPlayer.getCurrentAudioTime()) {
        if (duration && currentTime) {
            const parsedCurrentTime = this.parseTime(currentTime);
            const parsedDuration = this.parseTime(duration);
            return `${parsedCurrentTime} / ${parsedDuration}`;
        }
        return null;
    },
};

export default backgroundAudioService;
